interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number; // milisaniye cinsinden
}

class CacheService {
  private static instance: CacheService;
  private DEFAULT_EXPIRY = 5 * 60 * 1000; // 5 dakika varsayılan
  private AUTH_TOKEN_KEY = 'auth_token';
  private AUTH_TOKEN_EXPIRY = 24 * 60 * 60 * 1000; // 24 saat

  private constructor() {}

  public static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  // Veriyi önbelleğe al
  public setItem<T>(key: string, data: T, expiry: number = this.DEFAULT_EXPIRY): void {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiry
    };
    localStorage.setItem(key, JSON.stringify(cacheItem));
  }

  // Veriyi önbellekten al
  public getItem<T>(key: string): T | null {
    const item = localStorage.getItem(key);
    if (!item) return null;
    
    try {
      const cacheItem = JSON.parse(item) as CacheItem<T>;
      const now = Date.now();
      
      // Önbellek süresi dolmuş mu kontrol et
      if (now - cacheItem.timestamp > cacheItem.expiry) {
        localStorage.removeItem(key); // Süresi dolmuş veriyi temizle
        return null;
      }
      
      return cacheItem.data;
    } catch (error) {
      console.error("Önbellek verisi ayrıştırılamadı:", error);
      localStorage.removeItem(key);
      return null;
    }
  }

  // Önbellekten veriyi al, yoksa fetch fonksiyonunu çalıştır
  public async getOrFetch<T>(
    key: string, 
    fetchFn: () => Promise<T>,
    expiry: number = this.DEFAULT_EXPIRY
  ): Promise<T> {
    // Önbellekte var mı kontrol et
    const cachedData = this.getItem<T>(key);
    if (cachedData) {
      return cachedData;
    }
    
    // Yoksa API'den çek
    try {
      const data = await fetchFn();
      this.setItem<T>(key, data, expiry);
      return data;
    } catch (error) {
      console.error(`Veri çekilirken hata oluştu (${key}):`, error);
      throw error;
    }
  }

  // Belirli önbellek girdisini temizle
  public clear(key: string): void {
    localStorage.removeItem(key);
  }

  // Belirli bir önekle başlayan tüm önbellek girdilerini temizle
  public clearByPrefix(prefix: string): void {
    Object.keys(localStorage)
      .filter(key => key.startsWith(prefix))
      .forEach(key => localStorage.removeItem(key));
  }

  // Tüm önbelleği temizle
  public clearAll(): void {
    localStorage.clear();
  }

  // Önbelleği yenile - veriyi yeniden getir
  public async refresh<T>(
    key: string,
    fetchFn: () => Promise<T>,
    expiry: number = this.DEFAULT_EXPIRY
  ): Promise<T> {
    this.clear(key);
    return this.getOrFetch<T>(key, fetchFn, expiry);
  }

  // Auth token için özel metodlar
  public setAuthToken(token: string): void {
    this.setItem(this.AUTH_TOKEN_KEY, token, this.AUTH_TOKEN_EXPIRY);
  }

  public getAuthToken(): string | null {
    return this.getItem(this.AUTH_TOKEN_KEY);
  }

  public clearAuthToken(): void {
    this.clear(this.AUTH_TOKEN_KEY);
  }
}

export default CacheService.getInstance(); 